import { useEffect, useRef, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  authClient,
  useSession,
  useListOrganizations,
  useActiveOrganization,
  organization,
  signOut,
} from "@/lib/auth";
import { SignIn } from "@/pages/SignIn";
import { Button, Card } from "@/components/ui";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

/**
 * Route guard.
 *
 * Three states, and conflating any two of them produces a bug users report as
 * "it logged me out randomly":
 *
 *   loading  — we do not know yet. Render nothing decisive.
 *   no user  — show sign-in.
 *   no active workspace — signed in but not acting as anyone. This is a real
 *              state (fresh invitation, workspace removed under you), and it
 *              is what the API answers 403 "Select a workspace" for.
 *
 * This is convenience, not security. Every one of these checks is re-done on
 * the server; the client copy only decides what to paint.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { data: session, isPending: sessionPending } = useSession();
  const { data: organizations, isPending: orgsPending } = useListOrganizations();
  const { data: active, isPending: activePending } = useActiveOrganization();

  /**
   * Wait for every query, not just the session.
   *
   * These three resolve independently. Checking only the session — or only the
   * organization list — means there is a window where the list has arrived but
   * the active workspace has not, and `active` is null purely because its
   * request is still in flight. Rendering the picker there shows "No workspace
   * yet" to someone who has one, which then disappears on refresh: the bug
   * that looks like the app losing your account.
   */
  const stillLoading = sessionPending || orgsPending || activePending;

  if (stillLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg">
        <Loader2 size={20} className="animate-spin text-text-faint" />
      </div>
    );
  }

  if (!session?.user) return <SignIn />;

  // Only now is `active` being null meaningful: every query has settled.
  if (!active) {
    return <WorkspacePicker organizations={organizations ?? []} />;
  }

  return <>{children}</>;
}

function WorkspacePicker({
  organizations,
}: {
  organizations: Array<{ id: string; name: string }>;
}) {
  const { t } = useTranslation("shell");
  const claiming = useRef(false);

  /**
   * With exactly one workspace there is no choice to make, so select it rather
   * than showing a one-option picker.
   *
   * Two details keep this from looping, and it DID loop before both were here:
   *
   *  1. No `window.location.reload()`. The server caches the session in a
   *     cookie for five minutes, so a reload re-reads the *stale* session —
   *     still with no active organization — and lands straight back on this
   *     component. Instead, re-fetch the session with the cache bypassed and
   *     let the hooks re-render.
   *  2. The ref guard is not enough on its own, because a reload resets it.
   *     Removing the reload is what actually fixes it; the ref only stops a
   *     double-fire within one mount.
   */
  useEffect(() => {
    const only = organizations.length === 1 ? organizations[0] : undefined;
    if (!only || claiming.current) return;
    claiming.current = true;

    void (async () => {
      await organization.setActive({ organizationId: only.id });
      // Bypass the cookie cache so the very next read sees the new active org.
      await authClient.getSession({ query: { disableCookieCache: true } });
    })();
  }, [organizations]);

  if (organizations.length === 1) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg">
        <Loader2 size={20} className="animate-spin text-text-faint" />
      </div>
    );
  }

  if (organizations.length === 0) {
    return (
      <Centered
        title={t("workspace.noneTitle")}
        description={t("workspace.noneBody")}
        action={
          <Button onClick={() => void signOut()} variant="secondary">
            {t("nav.signOut")}
          </Button>
        }
      />
    );
  }

  return (
    <Centered
      title={t("workspace.chooseTitle")}
      description={t("workspace.chooseBody")}
      action={
        <div className="w-full space-y-1.5">
          {organizations.map((org) => (
            <Button
              key={org.id}
              variant="secondary"
              className="w-full justify-start"
              onClick={async () => {
                await organization.setActive({ organizationId: org.id });
                // Same reason as above: bypass the session cookie cache rather
                // than reloading, or the stale cookie sends us right back here.
                await authClient.getSession({ query: { disableCookieCache: true } });
              }}
            >
              {org.name}
            </Button>
          ))}
        </div>
      }
    />
  );
}

function Centered({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action: ReactNode;
}) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-bg px-5">
      <Card className="w-full max-w-[380px] p-6 text-center">
        <h1 className="text-base font-semibold text-text">{title}</h1>
        <p className="mt-1.5 text-sm leading-relaxed text-text-muted">{description}</p>
        <div className="mt-5 flex justify-center">{action}</div>
      </Card>
      <div className="mt-5">
        <LanguageSwitcher variant="select" />
      </div>
    </div>
  );
}
